import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { AppliedFilter, FilterDefinition } from '@hapana/shared/schemas/users/client-schemas'

export const CLIENT_FILTERS_STORAGE_KEY = 'hapana_client_filters'

interface ClientNavContext {
  ids: string[]
  listUrl: string
}

interface FiltersState {
  appliedFilters: AppliedFilter[]
  isDetailView: boolean
  selectedFilter: FilterDefinition | null
  editingFilterIndex: number | null
  smartGroupName: string
  smartGroupId: string | null
  urlSyncEnabled: boolean
  clientNavContext: ClientNavContext | null
  setAppliedFilters: (
    filters: AppliedFilter[] | ((prev: AppliedFilter[]) => AppliedFilter[]),
    syncURL?: boolean
  ) => void
  addFilter: (filter: AppliedFilter, syncURL?: boolean) => void
  updateFilter: (index: number, filter: AppliedFilter, syncURL?: boolean) => void
  removeFilterById: (filterId: string, syncURL?: boolean) => void
  clearFilters: (syncURL?: boolean) => void
  setIsDetailView: (isDetailView: boolean) => void
  setSelectedFilter: (filter: FilterDefinition | null) => void
  setEditingFilterIndex: (index: number | null) => void
  setSmartGroupName: (name: string) => void
  setSmartGroupId: (id: string | null) => void
  setUrlSyncEnabled: (enabled: boolean) => void
  setClientNavContext: (ctx: ClientNavContext) => void
  clearClientNavContext: () => void
  resetStore: () => void
}

export const useFiltersStore = create<FiltersState>()(
  devtools(
    (set, get) => ({
      appliedFilters: [],
      isDetailView: false,
      selectedFilter: null,
      editingFilterIndex: null,
      smartGroupName: '',
      smartGroupId: null,
      urlSyncEnabled: true,
      clientNavContext: null,

      setAppliedFilters: (filters, _syncURL = true) => {
        const newFilters = typeof filters === 'function' ? filters(get().appliedFilters) : filters
        // Always sort by orderIndex to maintain consistent order
        const sortedFilters = newFilters.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
        set(
          {
            appliedFilters: sortedFilters,
          },
          false,
          'setAppliedFilters'
        )
      },

      addFilter: (filter, _syncURL = true) =>
        set(
          (state) => ({
            appliedFilters: [...state.appliedFilters, filter],
          }),
          false,
          'addFilter'
        ),

      updateFilter: (index, filter, _syncURL = true) =>
        set(
          (state) => ({
            appliedFilters: state.appliedFilters.map((f, i) => (i === index ? filter : f)),
          }),
          false,
          'updateFilter'
        ),

      removeFilterById: (filterId, _syncURL = true) =>
        set(
          (state) => {
            // Remove the filter
            const filteredFilters = state.appliedFilters.filter((f) => f.id !== filterId)

            // Sort by orderIndex to maintain display order before recalculating
            const sortedFilters = filteredFilters.sort(
              (a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)
            )

            // Recalculate orderIndex sequentially (0, 1, 2, 3...)
            const reorderedFilters = sortedFilters.map((filter, index) => ({
              ...filter,
              orderIndex: index,
            }))

            return {
              appliedFilters: reorderedFilters,
            }
          },
          false,
          'removeFilterById'
        ),

      clearFilters: (_syncURL = true) => set({ appliedFilters: [] }, false, 'clearFilters'),

      setIsDetailView: (isDetailView) => set({ isDetailView }, false, 'setIsDetailView'),

      setSelectedFilter: (filter) => set({ selectedFilter: filter }, false, 'setSelectedFilter'),

      setEditingFilterIndex: (index) =>
        set({ editingFilterIndex: index }, false, 'setEditingFilterIndex'),

      setSmartGroupName: (name) => set({ smartGroupName: name }, false, 'setSmartGroupName'),

      setSmartGroupId: (id) => set({ smartGroupId: id }, false, 'setSmartGroupId'),

      setUrlSyncEnabled: (enabled) => set({ urlSyncEnabled: enabled }, false, 'setUrlSyncEnabled'),

      setClientNavContext: (ctx) => set({ clientNavContext: ctx }, false, 'setClientNavContext'),

      clearClientNavContext: () => set({ clientNavContext: null }, false, 'clearClientNavContext'),

      resetStore: () =>
        set(
          {
            appliedFilters: [],
            isDetailView: false,
            selectedFilter: null,
            editingFilterIndex: null,
            smartGroupName: '',
            smartGroupId: null,
            clientNavContext: null,
          },
          false,
          'resetStore'
        ),
    }),
    {
      name: 'filters-store',
    }
  )
)
